/**
 * Mission Calculator Utility
 *
 * Mathematical calculations for mission planning:
 * - Distance calculations (Haversine formula)
 * - Bearing calculations
 * - Flight time estimations
 * - Battery usage estimations
 * - Mission bounds calculations
 */

import type { Waypoint } from '../types/waypoint.types';
import type { Mission, MissionBounds, MissionStatistics, Position, VehicleType } from '../types/mission.types';
import { BATTERY_ESTIMATES, VALIDATION_LIMITS } from '@/constants/mission-defaults';

/**
 * Earth radius in meters (mean radius)
 */
const EARTH_RADIUS_METERS = 6371000;

/**
 * Convert degrees to radians
 */
function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Convert radians to degrees
 */
function toDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}

/**
 * Calculate great circle distance between two points using Haversine formula
 *
 * @param lat1 - Latitude of first point (degrees)
 * @param lon1 - Longitude of first point (degrees)
 * @param lat2 - Latitude of second point (degrees)
 * @param lon2 - Longitude of second point (degrees)
 * @returns Distance in meters
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const φ1 = toRadians(lat1);
  const φ2 = toRadians(lat2);
  const Δφ = toRadians(lat2 - lat1);
  const Δλ = toRadians(lon2 - lon1);

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_METERS * c;
}

/**
 * Calculate 3D distance including altitude difference
 *
 * @param wp1 - First waypoint
 * @param wp2 - Second waypoint
 * @returns 3D distance in meters
 */
export function calculate3DDistance(wp1: Waypoint, wp2: Waypoint): number {
  // Calculate horizontal distance (2D)
  const horizontalDistance = calculateDistance(
    wp1.latitude,
    wp1.longitude,
    wp2.latitude,
    wp2.longitude
  );

  // Calculate vertical distance (altitude difference)
  const verticalDistance = Math.abs(wp2.altitude - wp1.altitude);

  // Calculate 3D distance using Pythagorean theorem
  return Math.sqrt(
    horizontalDistance * horizontalDistance + verticalDistance * verticalDistance
  );
}

/**
 * Calculate bearing from point1 to point2
 *
 * @param lat1 - Latitude of first point (degrees)
 * @param lon1 - Longitude of first point (degrees)
 * @param lat2 - Latitude of second point (degrees)
 * @param lon2 - Longitude of second point (degrees)
 * @returns Bearing in degrees (0-360, where 0 is North)
 */
export function calculateBearing(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const φ1 = toRadians(lat1);
  const φ2 = toRadians(lat2);
  const Δλ = toRadians(lon2 - lon1);

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) -
    Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);

  const θ = Math.atan2(y, x);
  const bearing = (toDegrees(θ) + 360) % 360;

  return bearing;
}

/**
 * Calculate estimated flight time
 *
 * @param distance - Distance to travel in meters
 * @param speed - Speed in m/s
 * @param altitudeChange - Altitude change in meters (positive = climb)
 * @returns Estimated time in seconds
 */
export function calculateFlightTime(
  distance: number,
  speed: number,
  altitudeChange: number = 0
): number {
  // Base time based on horizontal distance
  let time = distance / speed;

  // Add extra time for altitude changes
  // Assume max climb/descent rate of 2.5 m/s for multicopter
  const maxVerticalSpeed = 2.5;
  const verticalTime = Math.abs(altitudeChange) / maxVerticalSpeed;

  // If climbing/descending while moving, use the longer of the two
  // Otherwise, add them together
  if (distance > 0) {
    time = Math.max(time, verticalTime);
  } else {
    time = verticalTime;
  }

  return time;
}

/**
 * Estimate battery consumption for a flight segment
 *
 * @param distance - Horizontal distance in meters
 * @param time - Flight time in seconds
 * @param altitudeChange - Altitude change in meters
 * @param vehicleType - Type of vehicle
 * @returns Estimated battery usage in percentage (0-100)
 */
export function estimateBatteryUsage(
  distance: number,
  time: number,
  altitudeChange: number,
  vehicleType: VehicleType
): number {
  const estimates = BATTERY_ESTIMATES[vehicleType];

  // Horizontal battery consumption
  const horizontalBattery = distance * estimates.horizontal;

  // Vertical battery consumption
  let verticalBattery = 0;
  if (altitudeChange > 0) {
    // Climbing
    verticalBattery = altitudeChange * estimates.vertical;
  } else if (altitudeChange < 0) {
    // Descending
    verticalBattery = Math.abs(altitudeChange) * estimates.descent;
  }

  // Hover/stationary battery consumption
  const hoverBattery = time * estimates.hover;

  // Total battery usage
  return horizontalBattery + verticalBattery + hoverBattery;
}

/**
 * Calculate total mission distance
 *
 * @param waypoints - Array of waypoints
 * @returns Total distance in meters
 */
export function calculateTotalDistance(waypoints: Waypoint[]): number {
  if (waypoints.length < 2) return 0;

  let totalDistance = 0;

  for (let i = 0; i < waypoints.length - 1; i++) {
    const distance = calculateDistance(
      waypoints[i].latitude,
      waypoints[i].longitude,
      waypoints[i + 1].latitude,
      waypoints[i + 1].longitude
    );
    totalDistance += distance;
  }

  return totalDistance;
}

/**
 * Calculate total 3D distance including altitude changes
 *
 * @param waypoints - Array of waypoints
 * @returns Total 3D distance in meters
 */
export function calculateTotal3DDistance(waypoints: Waypoint[]): number {
  if (waypoints.length < 2) return 0;

  let totalDistance = 0;

  for (let i = 0; i < waypoints.length - 1; i++) {
    totalDistance += calculate3DDistance(waypoints[i], waypoints[i + 1]);
  }

  return totalDistance;
}

/**
 * Calculate total altitude change in mission
 *
 * @param waypoints - Array of waypoints
 * @returns Total altitude change in meters
 */
export function calculateTotalAltitudeChange(waypoints: Waypoint[]): number {
  if (waypoints.length < 2) return 0;

  let totalChange = 0;

  for (let i = 0; i < waypoints.length - 1; i++) {
    totalChange += Math.abs(waypoints[i + 1].altitude - waypoints[i].altitude);
  }

  return totalChange;
}

/**
 * Calculate altitude statistics
 *
 * @param waypoints - Array of waypoints
 * @returns Object with min, max, and average altitude
 */
export function calculateAltitudeStats(waypoints: Waypoint[]): {
  min: number;
  max: number;
  avg: number;
} {
  if (waypoints.length === 0) {
    return { min: 0, max: 0, avg: 0 };
  }

  const altitudes = waypoints.map((wp) => wp.altitude);
  const min = Math.min(...altitudes);
  const max = Math.max(...altitudes);
  const avg = altitudes.reduce((sum, alt) => sum + alt, 0) / altitudes.length;

  return { min, max, avg };
}

/**
 * Calculate mission bounds (bounding box)
 *
 * @param waypoints - Array of waypoints
 * @returns Bounding box coordinates
 */
export function calculateMissionBounds(waypoints: Waypoint[]): MissionBounds {
  if (waypoints.length === 0) {
    return {
      north: 0,
      south: 0,
      east: 0,
      west: 0,
    };
  }

  const latitudes = waypoints.map((wp) => wp.latitude);
  const longitudes = waypoints.map((wp) => wp.longitude);

  return {
    north: Math.max(...latitudes),
    south: Math.min(...latitudes),
    east: Math.max(...longitudes),
    west: Math.min(...longitudes),
  };
}

/**
 * Calculate comprehensive mission statistics
 *
 * @param mission - Mission object
 * @returns Complete mission statistics
 */
export function calculateMissionStatistics(mission: Mission): MissionStatistics {
  const { waypoints, cruiseSpeed, vehicleType } = mission;

  if (waypoints.length === 0) {
    return {
      waypointCount: 0,
      totalDistance: 0,
      horizontalDistance: 0,
      verticalDistance: 0,
      estimatedFlightTime: 0,
      estimatedBatteryUsage: 0,
      maxAltitude: 0,
      minAltitude: 0,
      averageAltitude: 0,
      averageSpeed: cruiseSpeed,
    };
  }

  // Calculate distances
  const horizontalDistance = calculateTotalDistance(waypoints);
  const totalDistance = calculateTotal3DDistance(waypoints);
  const verticalDistance = calculateTotalAltitudeChange(waypoints);

  // Calculate altitude stats
  const altitudeStats = calculateAltitudeStats(waypoints);

  // Calculate estimated time
  let estimatedTime = 0;
  let totalBattery = 0;

  for (let i = 0; i < waypoints.length - 1; i++) {
    const wp1 = waypoints[i];
    const wp2 = waypoints[i + 1];

    const segmentDistance = calculateDistance(
      wp1.latitude,
      wp1.longitude,
      wp2.latitude,
      wp2.longitude
    );
    const altitudeChange = wp2.altitude - wp1.altitude;

    const segmentTime = calculateFlightTime(segmentDistance, cruiseSpeed, altitudeChange);
    estimatedTime += segmentTime;

    const segmentBattery = estimateBatteryUsage(
      segmentDistance,
      segmentTime,
      altitudeChange,
      vehicleType
    );
    totalBattery += segmentBattery;

    // Add hold time at waypoint (param1)
    estimatedTime += wp2.param1;
    totalBattery += wp2.param1 * BATTERY_ESTIMATES[vehicleType].hover;
  }

  return {
    waypointCount: waypoints.length,
    totalDistance,
    horizontalDistance,
    verticalDistance,
    estimatedFlightTime: estimatedTime,
    estimatedBatteryUsage: Math.min(100, totalBattery), // Cap at 100%
    maxAltitude: altitudeStats.max,
    minAltitude: altitudeStats.min,
    averageAltitude: altitudeStats.avg,
    averageSpeed: cruiseSpeed,
  };
}

/**
 * Calculate midpoint between two positions
 *
 * @param pos1 - First position
 * @param pos2 - Second position
 * @returns Midpoint position
 */
export function calculateMidpoint(pos1: Position, pos2: Position): Position {
  const φ1 = toRadians(pos1.latitude);
  const λ1 = toRadians(pos1.longitude);
  const φ2 = toRadians(pos2.latitude);
  const λ2 = toRadians(pos2.longitude);

  const Bx = Math.cos(φ2) * Math.cos(λ2 - λ1);
  const By = Math.cos(φ2) * Math.sin(λ2 - λ1);

  const φ3 = Math.atan2(
    Math.sin(φ1) + Math.sin(φ2),
    Math.sqrt((Math.cos(φ1) + Bx) * (Math.cos(φ1) + Bx) + By * By)
  );
  const λ3 = λ1 + Math.atan2(By, Math.cos(φ1) + Bx);

  return {
    latitude: toDegrees(φ3),
    longitude: toDegrees(λ3),
    altitude: (pos1.altitude + pos2.altitude) / 2,
  };
}

/**
 * Format distance for display
 *
 * @param meters - Distance in meters
 * @returns Formatted string (e.g., "1.5 km" or "250 m")
 */
export function formatDistance(meters: number): string {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2)} km`;
  }
  return `${meters.toFixed(0)} m`;
}

/**
 * Format time for display
 *
 * @param seconds - Time in seconds
 * @returns Formatted string (e.g., "5m 30s" or "1h 20m")
 */
export function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

/**
 * Format battery percentage for display
 *
 * @param percentage - Battery percentage (0-100)
 * @returns Formatted string with color indicator
 */
export function formatBattery(percentage: number): {
  text: string;
  color: 'green' | 'yellow' | 'red';
} {
  const text = `${percentage.toFixed(1)}%`;

  let color: 'green' | 'yellow' | 'red';
  if (percentage < VALIDATION_LIMITS.minBatteryReserve) {
    color = 'red'; // Critical - not enough reserve
  } else if (percentage < 50) {
    color = 'yellow'; // Warning
  } else {
    color = 'green'; // Good
  }

  return { text, color };
}
